import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  FileText, Download, Plus, Trash2, Edit2, Check, X, Send,
  Building2, DollarSign, Clock, Calendar, RefreshCw, Receipt,
  AlertTriangle, CheckCircle2, TrendingUp, CreditCard, FileSignature,
  RotateCcw, ChevronRight, Eye
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContractManagement = () => {
  const navigate = useNavigate();
  const { contractId } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [contracts, setContracts] = useState([]);
  const [currentContract, setCurrentContract] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [clients, setClients] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [activeView, setActiveView] = useState('list');
  const [activeTab, setActiveTab] = useState('overview');
  
  const [newContract, setNewContract] = useState({
    clientId: '',
    title: '',
    description: '',
    value: 0,
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    billingCycle: 'monthly',
    autoRenew: true,
    renewalTermMonths: 12,
    terms: '',
    notes: ''
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('cset_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (contractId) {
      fetchContract(contractId);
      setActiveView('detail');
    }
  }, [contractId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [contractsRes, dashboardRes, clientsRes] = await Promise.all([
        fetch(`${API_URL}/api/contracts`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/contracts/dashboard`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/msp/portfolio/overview`, { headers: getAuthHeaders() })
      ]);

      if (contractsRes.ok) {
        const data = await contractsRes.json();
        setContracts(data.contracts || []);
      }

      if (dashboardRes.ok) {
        const data = await dashboardRes.json();
        setDashboard(data);
      }

      if (clientsRes.ok) {
        const data = await clientsRes.json();
        setClients(data.clients || []);
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchContract = async (id) => {
    try {
      const response = await fetch(`${API_URL}/api/contracts/${id}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setCurrentContract(data);
      }
    } catch (err) {
      console.error('Failed to fetch contract:', err);
    }
  };

  const handleCreateContract = async () => {
    if (!newContract.clientId || !newContract.title || !newContract.value) {
      alert('Please fill in required fields');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/contracts`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(newContract)
      });

      if (response.ok) {
        const data = await response.json();
        setShowCreateModal(false);
        setNewContract({
          clientId: '',
          title: '',
          description: '',
          value: 0,
          startDate: new Date().toISOString().split('T')[0],
          endDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          billingCycle: 'monthly',
          autoRenew: true,
          renewalTermMonths: 12,
          terms: '',
          notes: ''
        });
        fetchData();
        navigate(`/contracts/${data.contract.id}`);
      }
    } catch (err) {
      console.error('Failed to create contract:', err);
    }
  };

  const handleSignContract = async (id) => {
    try {
      const response = await fetch(`${API_URL}/api/contracts/${id}/sign`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        fetchData();
        if (currentContract?.id === id) {
          fetchContract(id);
        }
      }
    } catch (err) {
      console.error('Failed to sign contract:', err);
    }
  };

  const handleRenewContract = async (id) => {
    if (!confirm('Are you sure you want to renew this contract?')) return;

    try {
      const response = await fetch(`${API_URL}/api/contracts/${id}/renew`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        fetchData();
        if (currentContract?.id === id) {
          fetchContract(id);
        }
      }
    } catch (err) {
      console.error('Failed to renew contract:', err);
    }
  };

  const handleGenerateInvoice = async (id) => {
    try {
      const response = await fetch(`${API_URL}/api/contracts/${id}/invoices/generate`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        fetchContract(id);
        fetchData();
      }
    } catch (err) {
      console.error('Failed to generate invoice:', err);
    }
  };

  const handleMarkInvoicePaid = async (invoiceId) => {
    try {
      const response = await fetch(`${API_URL}/api/contracts/invoices/${invoiceId}/pay`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        if (currentContract) {
          fetchContract(currentContract.id);
        }
        fetchData();
      }
    } catch (err) {
      console.error('Failed to mark invoice paid:', err);
    }
  };

  const handleDownloadContract = async (id) => {
    try {
      const response = await fetch(`${API_URL}/api/contracts/${id}/download`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Contract_${id}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  const handleDownloadInvoice = async (invoiceId) => {
    try {
      const response = await fetch(`${API_URL}/api/contracts/invoices/${invoiceId}/download`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Invoice_${invoiceId}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  const handleDeleteContract = async (id) => {
    if (!confirm('Are you sure you want to delete this contract? This will also delete all associated invoices.')) return;

    try {
      const response = await fetch(`${API_URL}/api/contracts/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        fetchData();
        if (currentContract?.id === id) {
          setCurrentContract(null);
          setActiveView('list');
          navigate('/contracts');
        }
      }
    } catch (err) {
      console.error('Failed to delete contract:', err);
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'active': return 'emerald';
      case 'expiring': return 'amber';
      case 'expired': return 'red';
      case 'pending': return 'blue';
      default: return 'slate';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const ContractCard = ({ contract }) => {
    const statusColor = getStatusColor(contract.status);
    
    return (
      <div 
        className="bg-slate-800/50 rounded-xl p-5 border border-slate-700/50 hover:border-violet-500/30 transition-all cursor-pointer group"
        onClick={() => { setActiveView('detail'); navigate(`/contracts/${contract.id}`); }}
        data-testid={`contract-card-${contract.id}`}
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="text-xs text-slate-500 font-mono">{contract.contractNumber}</p>
            <h3 className="text-lg font-semibold text-white mt-1 group-hover:text-violet-300 transition-colors">
              {contract.title}
            </h3>
            <p className="text-sm text-slate-400">{contract.clientName}</p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <span className={`px-3 py-1 rounded-full text-xs font-medium bg-${statusColor}-500/20 text-${statusColor}-400 border border-${statusColor}-500/30 capitalize`}>
              {contract.status}
            </span>
            {contract.autoRenew && (
              <span className="px-2 py-0.5 rounded text-xs bg-blue-500/20 text-blue-400">Auto-Renew</span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-4">
          <div>
            <p className="text-xs text-slate-500">Value</p>
            <p className="text-lg font-bold text-violet-400">{formatCurrency(contract.value)}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Billing</p>
            <p className="text-sm text-white capitalize">{contract.billingCycle}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500">MRR</p>
            <p className="text-sm text-emerald-400">{formatCurrency(contract.monthlyValue)}</p>
          </div>
        </div>

        <div className="flex items-center justify-between pt-3 border-t border-slate-700/50 text-xs text-slate-500">
          <span>Ends: {contract.endDate}</span>
          <span>{contract.invoiceCount || 0} invoices</span>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-violet-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">Loading contracts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 p-6" data-testid="contract-management-page">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center">
                <FileSignature className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Contract Management</h1>
                <p className="text-slate-400">Manage contracts, renewals, and billing</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/proposals')}
              className="flex items-center gap-2 px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white hover:bg-slate-700 transition-colors"
            >
              <FileText className="w-4 h-4" />
              Proposals
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white rounded-lg transition-colors"
              data-testid="create-contract-btn"
            >
              <Plus className="w-4 h-4" />
              New Contract
            </button>
          </div>
        </div>
      </div>

      {/* Dashboard Stats */}
      {dashboard && activeView === 'list' && (
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
            <p className="text-2xl font-bold text-white">{dashboard.totalContracts}</p>
            <p className="text-sm text-slate-400">Total Contracts</p>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-emerald-500/30">
            <p className="text-2xl font-bold text-emerald-400">{dashboard.activeContracts}</p>
            <p className="text-sm text-slate-400">Active</p>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-amber-500/30">
            <p className="text-2xl font-bold text-amber-400">{dashboard.expiringContracts}</p>
            <p className="text-sm text-slate-400">Expiring Soon</p>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-violet-500/30">
            <p className="text-2xl font-bold text-violet-400">{formatCurrency(dashboard.activeContractValue)}</p>
            <p className="text-sm text-slate-400">Active Value</p>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-cyan-500/30">
            <p className="text-2xl font-bold text-cyan-400">{formatCurrency(dashboard.monthlyRecurringRevenue)}</p>
            <p className="text-sm text-slate-400">MRR</p>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-orange-500/30">
            <p className="text-2xl font-bold text-orange-400">{formatCurrency(dashboard.outstandingBalance)}</p>
            <p className="text-sm text-slate-400">Outstanding</p>
          </div>
        </div>
      )}

      {/* Upcoming Renewals Alert */}
      {dashboard?.upcomingRenewals?.length > 0 && activeView === 'list' && (
        <div className="max-w-7xl mx-auto mb-8">
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              <h3 className="font-semibold text-amber-400">Upcoming Renewals</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {dashboard.upcomingRenewals.slice(0, 3).map((renewal) => (
                <div 
                  key={renewal.id}
                  className="bg-slate-800/50 rounded-lg p-3 cursor-pointer hover:bg-slate-800/70"
                  onClick={() => navigate(`/contracts/${renewal.id}`)}
                >
                  <p className="text-sm font-medium text-white">{renewal.clientName}</p>
                  <p className="text-xs text-slate-400">{renewal.title}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-amber-400">Ends: {renewal.endDate}</span>
                    <span className="text-sm text-violet-400">{formatCurrency(renewal.value)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      {activeView === 'list' && (
        <div className="max-w-7xl mx-auto">
          {contracts.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {contracts.map((contract) => (
                <ContractCard key={contract.id} contract={contract} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <FileSignature className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No Contracts Yet</h3>
              <p className="text-slate-400 mb-6">Create your first contract or convert a proposal</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-6 py-3 bg-violet-600 hover:bg-violet-500 text-white rounded-lg transition-colors"
              >
                Create First Contract
              </button>
            </div>
          )}
        </div>
      )}

      {/* Contract Detail View */}
      {activeView === 'detail' && currentContract && (
        <div className="max-w-5xl mx-auto">
          {/* Contract Header */}
          <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50 mb-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-sm text-slate-500 font-mono mb-1">{currentContract.contractNumber}</p>
                <h2 className="text-2xl font-bold text-white">{currentContract.title}</h2>
                <p className="text-slate-400 mt-1">Client: {currentContract.clientName}</p>
              </div>
              <div className="flex flex-col items-end gap-2">
                <span className={`px-4 py-2 rounded-lg text-sm font-medium bg-${getStatusColor(currentContract.status)}-500/20 text-${getStatusColor(currentContract.status)}-400 border border-${getStatusColor(currentContract.status)}-500/30 capitalize`}>
                  {currentContract.status}
                </span>
                {currentContract.isSigned && (
                  <span className="flex items-center gap-1 text-xs text-emerald-400">
                    <CheckCircle2 className="w-3 h-3" /> Signed {currentContract.signedAt?.split('T')[0]}
                  </span>
                )}
              </div>
            </div>

            {currentContract.description && (
              <p className="text-slate-300 mb-4">{currentContract.description}</p>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-900/50 rounded-lg">
              <div>
                <p className="text-xs text-slate-500">Contract Value</p>
                <p className="text-xl font-bold text-violet-400">{formatCurrency(currentContract.value)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Monthly Value</p>
                <p className="text-lg text-emerald-400">{formatCurrency(currentContract.monthlyValue)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Billing Cycle</p>
                <p className="text-white capitalize">{currentContract.billingCycle}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Auto-Renew</p>
                <p className={currentContract.autoRenew ? 'text-emerald-400' : 'text-slate-400'}>
                  {currentContract.autoRenew ? 'Yes' : 'No'}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4 mt-4 text-sm text-slate-400">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {currentContract.startDate} to {currentContract.endDate}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                Next billing: {currentContract.nextBillingDate}
              </span>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-2 mb-6 border-b border-slate-700">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === 'overview' 
                  ? 'text-violet-400 border-b-2 border-violet-400' 
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('invoices')}
              className={`px-4 py-3 text-sm font-medium transition-colors flex items-center gap-2 ${
                activeTab === 'invoices' 
                  ? 'text-violet-400 border-b-2 border-violet-400' 
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              Invoices
              <span className="px-2 py-0.5 bg-slate-700 text-slate-300 text-xs rounded">
                {currentContract.invoices?.length || 0}
              </span>
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Services from Proposal */}
              {currentContract.proposalData?.services?.length > 0 && (
                <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden">
                  <div className="p-4 border-b border-slate-700/50">
                    <h3 className="text-lg font-semibold text-white">Included Services</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-900/50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Service</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase">Hours</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 uppercase">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-700/50">
                        {currentContract.proposalData.services.map((service, index) => (
                          <tr key={index} className="hover:bg-slate-800/50">
                            <td className="px-4 py-3">
                              <p className="text-white font-medium">{service.name}</p>
                              <p className="text-xs text-slate-400">{service.description}</p>
                            </td>
                            <td className="px-4 py-3 text-center text-slate-300">
                              {service.hours > 0 ? service.hours : '-'}
                            </td>
                            <td className="px-4 py-3 text-right text-white font-medium">
                              {formatCurrency(service.total)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Billing Summary */}
              <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
                <h3 className="text-lg font-semibold text-white mb-4">Billing Summary</h3>
                <div className="grid grid-cols-3 gap-6">
                  <div className="text-center p-4 bg-slate-900/50 rounded-lg">
                    <Receipt className="w-6 h-6 text-violet-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-white">{formatCurrency(currentContract.totalInvoiced)}</p>
                    <p className="text-sm text-slate-400">Total Invoiced</p>
                  </div>
                  <div className="text-center p-4 bg-slate-900/50 rounded-lg">
                    <CreditCard className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-emerald-400">{formatCurrency(currentContract.totalPaid)}</p>
                    <p className="text-sm text-slate-400">Total Paid</p>
                  </div>
                  <div className="text-center p-4 bg-slate-900/50 rounded-lg">
                    <DollarSign className="w-6 h-6 text-orange-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-orange-400">
                      {formatCurrency((currentContract.totalInvoiced || 0) - (currentContract.totalPaid || 0))}
                    </p>
                    <p className="text-sm text-slate-400">Outstanding</p>
                  </div>
                </div>
              </div>

              {/* Terms */}
              {currentContract.terms && (
                <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
                  <h3 className="text-lg font-semibold text-white mb-3">Terms & Conditions</h3>
                  <p className="text-slate-300 whitespace-pre-wrap">{currentContract.terms}</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'invoices' && (
            <div className="space-y-4">
              {/* Generate Invoice Button */}
              <div className="flex justify-end">
                <button
                  onClick={() => handleGenerateInvoice(currentContract.id)}
                  className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg transition-colors"
                >
                  <Receipt className="w-4 h-4" />
                  Generate Invoice
                </button>
              </div>

              {/* Invoice List */}
              {currentContract.invoices?.length > 0 ? (
                <div className="space-y-3">
                  {currentContract.invoices.map((invoice) => (
                    <div 
                      key={invoice.id}
                      className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50 flex items-center justify-between"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          invoice.status === 'paid' ? 'bg-emerald-500/20' : 'bg-amber-500/20'
                        }`}>
                          <Receipt className={`w-5 h-5 ${
                            invoice.status === 'paid' ? 'text-emerald-400' : 'text-amber-400'
                          }`} />
                        </div>
                        <div>
                          <p className="text-white font-medium">{invoice.invoiceNumber}</p>
                          <p className="text-sm text-slate-400">{invoice.description}</p>
                          <p className="text-xs text-slate-500">Due: {invoice.dueDate}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-lg font-bold text-white">{formatCurrency(invoice.amount)}</p>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            invoice.status === 'paid' 
                              ? 'bg-emerald-500/20 text-emerald-400' 
                              : 'bg-amber-500/20 text-amber-400'
                          }`}>
                            {invoice.status.toUpperCase()}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {invoice.status !== 'paid' && (
                            <button
                              onClick={() => handleMarkInvoicePaid(invoice.id)}
                              className="p-2 bg-emerald-500/20 text-emerald-400 rounded-lg hover:bg-emerald-500/30"
                              title="Mark as Paid"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleDownloadInvoice(invoice.id)}
                            className="p-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600"
                            title="Download PDF"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Receipt className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">No invoices yet</p>
                  <p className="text-sm text-slate-500">Generate an invoice to start billing</p>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between mt-8">
            <button
              onClick={() => { setActiveView('list'); navigate('/contracts'); }}
              className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
            >
              Back to List
            </button>

            <div className="flex items-center gap-3">
              <button
                onClick={() => handleDeleteContract(currentContract.id)}
                className="flex items-center gap-2 px-4 py-2 bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>

              <button
                onClick={() => handleDownloadContract(currentContract.id)}
                className="flex items-center gap-2 px-4 py-2 bg-slate-800 border border-slate-700 text-white rounded-lg hover:bg-slate-700 transition-colors"
              >
                <Download className="w-4 h-4" />
                Download PDF
              </button>

              {!currentContract.isSigned && (
                <button
                  onClick={() => handleSignContract(currentContract.id)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors"
                >
                  <FileSignature className="w-4 h-4" />
                  Mark as Signed
                </button>
              )}

              {(currentContract.status === 'expiring' || currentContract.status === 'expired') && (
                <button
                  onClick={() => handleRenewContract(currentContract.id)}
                  className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                  Renew Contract
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create Contract Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-700">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-white">Create New Contract</h2>
                <button onClick={() => setShowCreateModal(false)} className="text-slate-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Client Selection */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Select Client *</label>
                <select
                  value={newContract.clientId}
                  onChange={(e) => setNewContract({ ...newContract, clientId: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-violet-500"
                >
                  <option value="">Choose a client...</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>{client.name}</option>
                  ))}
                </select>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Contract Title *</label>
                <input
                  type="text"
                  value={newContract.title}
                  onChange={(e) => setNewContract({ ...newContract, title: e.target.value })}
                  placeholder="e.g., Annual Managed Security Services"
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-violet-500"
                />
              </div>

              {/* Value */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Contract Value ($) *</label>
                <input
                  type="number"
                  min="0"
                  step="100"
                  value={newContract.value}
                  onChange={(e) => setNewContract({ ...newContract, value: parseFloat(e.target.value) || 0 })}
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-violet-500"
                />
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Start Date *</label>
                  <input
                    type="date"
                    value={newContract.startDate}
                    onChange={(e) => setNewContract({ ...newContract, startDate: e.target.value })}
                    className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-violet-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">End Date *</label>
                  <input
                    type="date"
                    value={newContract.endDate}
                    onChange={(e) => setNewContract({ ...newContract, endDate: e.target.value })}
                    className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-violet-500"
                  />
                </div>
              </div>

              {/* Billing Cycle */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Billing Cycle</label>
                  <select
                    value={newContract.billingCycle}
                    onChange={(e) => setNewContract({ ...newContract, billingCycle: e.target.value })}
                    className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-violet-500"
                  >
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="annually">Annually</option>
                    <option value="one-time">One-Time</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Renewal Term (Months)</label>
                  <input
                    type="number"
                    min="1"
                    max="60"
                    value={newContract.renewalTermMonths}
                    onChange={(e) => setNewContract({ ...newContract, renewalTermMonths: parseInt(e.target.value) || 12 })}
                    className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-violet-500"
                  />
                </div>
              </div>

              {/* Auto-Renew */}
              <label className="flex items-center gap-3 p-3 bg-slate-900 rounded-lg cursor-pointer">
                <input
                  type="checkbox"
                  checked={newContract.autoRenew}
                  onChange={(e) => setNewContract({ ...newContract, autoRenew: e.target.checked })}
                  className="w-5 h-5 rounded border-slate-600 text-violet-500 focus:ring-violet-500"
                />
                <div>
                  <p className="text-white font-medium">Auto-Renew</p>
                  <p className="text-xs text-slate-400">Automatically renew contract before expiration</p>
                </div>
              </label>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Description</label>
                <textarea
                  value={newContract.description}
                  onChange={(e) => setNewContract({ ...newContract, description: e.target.value })}
                  placeholder="Contract description..."
                  rows={2}
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-violet-500"
                />
              </div>

              {/* Terms */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Terms & Conditions</label>
                <textarea
                  value={newContract.terms}
                  onChange={(e) => setNewContract({ ...newContract, terms: e.target.value })}
                  placeholder="Contract terms..."
                  rows={3}
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-violet-500"
                />
              </div>
            </div>

            <div className="p-6 border-t border-slate-700 flex justify-end gap-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateContract}
                disabled={!newContract.clientId || !newContract.title || !newContract.value}
                className="flex items-center gap-2 px-6 py-2 bg-violet-600 hover:bg-violet-500 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                <FileSignature className="w-4 h-4" />
                Create Contract
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractManagement;
